// ClubLandingPage Component
// Public-facing club page with shareable URL
// Redesigned: Sports Broadcast Style

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  FaArrowLeft, 
  FaShare, 
  FaEdit,
  FaBell,
  FaMapMarkerAlt, 
  FaPhone, 
  FaEnvelope,
  FaUsers,
  FaFutbol,
  FaBasketballBall,
  FaCalendarAlt,
  FaTrophy,
  FaGraduationCap,
  FaDumbbell,
  FaCheck,
  FaCopy,
  FaExternalLinkAlt,
  FaLock,
  FaDollarSign,
  FaUserPlus,
  FaChevronRight,
  FaPlay,
  FaClock
} from 'react-icons/fa';
import { ClipLoader } from 'react-spinners';
import { useUser } from '../../context/UserContext';
import { useNotification } from '../../context/NotificationContext';
import JoinClubModal from '../../components/Club/JoinClubModal';
import ClubManagementModal from '../../components/Club/ClubManagementModal';
import styles from './ClubLandingPage.module.css';

// Helper to extract URL from banner object
const getBannerUrl = (bannerImage) => {
  if (!bannerImage) return null;
  if (typeof bannerImage === 'string') return bannerImage;
  if (bannerImage.transformedUrls?.full) return bannerImage.transformedUrls.full;
  if (bannerImage.transformedUrls?.banner) return bannerImage.transformedUrls.banner;
  if (bannerImage.url) return bannerImage.url;
  if (bannerImage.secure_url) return bannerImage.secure_url;
  if (bannerImage.cloudinary?.url) return bannerImage.cloudinary.url;
  if (bannerImage.cloudinary?.secure_url) return bannerImage.cloudinary.secure_url;
  return null;
};

// Sport icon mapping
const sportIcons = {
  Soccer: FaFutbol,
  Basketball: FaBasketballBall,
  Baseball: FaFutbol,
  Football: FaFutbol,
  Tennis: FaFutbol,
  default: FaFutbol
};

// Category icon mapping
const categoryIcons = {
  Recreational: FaUsers,
  Competitive: FaTrophy,
  Academy: FaGraduationCap,
  Leagues: FaTrophy,
  Camps: FaCalendarAlt,
  Trainings: FaDumbbell,
  default: FaFutbol
};

const ClubLandingPage = () => {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userClubs, refreshUserClubs } = useUser();
  const { showSuccess, showError } = useNotification();
  
  const [club, setClub] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [memberCount, setMemberCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [showManagementModal, setShowManagementModal] = useState(false);
  const [managementInitialTab, setManagementInitialTab] = useState('members');
  const [managementModalKey, setManagementModalKey] = useState(0);
  const [copied, setCopied] = useState(false);
  const [bannerError, setBannerError] = useState(false);

  const shareMenuRef = useRef(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

  // Close share menu when clicking outside
  const handleClickOutside = useCallback((event) => {
    if (shareMenuRef.current && !shareMenuRef.current.contains(event.target)) {
      setShowShareMenu(false);
    }
  }, []);

  useEffect(() => {
    if (showShareMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showShareMenu, handleClickOutside]);

  // Handle navigation state from notifications - wait for club data to load first
  useEffect(() => {
    if (location.state?.openModal === 'management' && club && !loading) {
      // Set tab first, then open modal with new key to force remount
      if (location.state?.activeTab) {
        setManagementInitialTab(location.state.activeTab);
      }
      setManagementModalKey(prev => prev + 1);
      setShowManagementModal(true);
      // Clear the navigation state
      window.history.replaceState({}, document.title);
    }
  }, [location.state, club, loading]);

  // Fetch club data
  useEffect(() => {
    const fetchClubData = async () => {
      try {
        setLoading(true);
        setError(null);

        const clubRes = await fetch(`${backendUrl}/api/clubs/${clubId}`);
        if (!clubRes.ok) throw new Error('Club not found');
        const clubData = await clubRes.json();
        setClub(clubData);

        const programsRes = await fetch(`${backendUrl}/api/programs/club/${clubId}`);
        if (programsRes.ok) {
          const programsData = await programsRes.json();
          setPrograms(programsData);
        }

        const categoriesRes = await fetch(`${backendUrl}/api/categories/club/${clubId}`);
        if (categoriesRes.ok) {
          const categoriesData = await categoriesRes.json();
          setCategories(categoriesData);
        }

        // Fetch member count
        const membersRes = await fetch(`${backendUrl}/api/clubs/${clubId}/members?limit=1`);
        if (membersRes.ok) {
          const membersData = await membersRes.json();
          // The API returns pagination with total count, or we get the stats
          if (membersData.pagination?.total !== undefined) {
            setMemberCount(membersData.pagination.total);
          } else if (membersData.stats?.total !== undefined) {
            setMemberCount(membersData.stats.total);
          } else if (Array.isArray(membersData.members)) {
            setMemberCount(membersData.members.length);
          } else if (Array.isArray(membersData)) {
            setMemberCount(membersData.length);
          }
        }

      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (clubId) {
      fetchClubData();
    }
  }, [clubId, backendUrl]);

  // Check if user is a club member/manager
  const userMembership = userClubs?.find(m => {
    const membershipClubId = m.club?._id || m.club;
    return String(membershipClubId) === String(clubId);
  });
  const userRole = userMembership?.role;
  const membershipStatus = userMembership?.status?.toLowerCase();
  const isManager = ['Creator', 'Owner', 'Manager', 'Admin'].includes(userRole);
  
  // User is only a member if they have an ACTIVE membership status
  // Must be 'active', 'approved', or 'member' - NOT 'invited', 'pending', or undefined
  const isActiveMemberStatus = membershipStatus === 'active' || membershipStatus === 'approved' || membershipStatus === 'member';
  const isMember = !!userMembership && isActiveMemberStatus;

  // Get program count by category
  const getProgramCountByCategory = (categoryId) => {
    if (categoryId === 'all') return programs.length;
    return programs.filter(p => 
      p.category?._id === categoryId || p.category === categoryId
    ).length;
  };

  // Get sport icon
  const SportIcon = sportIcons[club?.sport] || sportIcons.default;

  // Handle share
  const handleShare = async (method) => {
    const url = window.location.href;
    const text = `Check out ${club?.name} on Sport-Loop!`;

    switch (method) {
      case 'copy':
        try {
          await navigator.clipboard.writeText(url);
          setCopied(true);
          showSuccess('Link copied to clipboard!');
          setTimeout(() => setCopied(false), 2000);
        } catch (err) {
          showError('Failed to copy link');
        }
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'email':
        window.location.href = `mailto:?subject=${encodeURIComponent(text)}&body=${encodeURIComponent(url)}`;
        break;
      default:
        break;
    }
    setShowShareMenu(false);
  };

  // Handle join success
  const handleJoinSuccess = () => {
    setShowJoinModal(false);
    refreshUserClubs();
    showSuccess('Successfully joined the club!');
  };

  // Loading state
  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <ClipLoader color="#A4EB3F" size={50} />
        <p>Loading club...</p>
      </div>
    );
  }

  // Error state
  if (error || !club) {
    return (
      <div className={styles.errorContainer}>
        <h2>Club Not Found</h2>
        <p>Sorry, we couldn't find this club.</p>
        <button className={styles.backButton} onClick={() => navigate('/')}>
          <FaArrowLeft /> Back to Home
        </button>
      </div>
    );
  }

  // Get membership type display
  const getMembershipDisplay = () => {
    switch (club.membershipType) {
      case 'free_open':
        return { text: 'Free • Open', icon: FaUsers };
      case 'free_approval':
        return { text: 'Approval Required', icon: FaLock };
      case 'paid':
        return { text: `$${club.membershipFee || 0}/yr`, icon: FaDollarSign };
      default:
        return { text: 'Open', icon: FaUsers };
    }
  };

  const membershipInfo = getMembershipDisplay();
  const clubColor = club.primaryColor || '#A4EB3F';

  return (
    <div className={styles.page} style={{ '--club-color': clubColor }}>
      
      {/* ═══════════════════════════════════════════════════
          HERO SECTION - Full Impact
      ═══════════════════════════════════════════════════ */}
      <section className={styles.hero}>
        {/* Background */}
        {getBannerUrl(club.bannerImage) && !bannerError ? (
          <div className={styles.heroBg}>
            <img 
              src={getBannerUrl(club.bannerImage)} 
              alt="" 
              className={styles.heroBgImg}
              onError={() => setBannerError(true)}
            />
            <div className={styles.heroBgOverlay} />
          </div>
        ) : (
          <div className={styles.heroBgFallback} />
        )}

        {/* Top Nav */}
        <nav className={styles.topNav} aria-label="Club page navigation">
          <button 
            className={styles.navBtn} 
            onClick={() => navigate('/')}
            aria-label="Go back to home"
          >
            <FaArrowLeft /> <span>Back</span>
          </button>
          <div className={styles.navRight}>
            <div className={styles.shareWrapper} ref={shareMenuRef}>
              <button 
                className={styles.navBtn} 
                onClick={() => setShowShareMenu(!showShareMenu)}
                aria-expanded={showShareMenu}
                aria-haspopup="true"
              >
                <FaShare /> <span>Share</span>
              </button>
              {showShareMenu && (
                <div className={styles.shareDropdown} role="menu">
                  <button onClick={() => handleShare('copy')} role="menuitem">
                    {copied ? <FaCheck /> : <FaCopy />} {copied ? 'Copied!' : 'Copy Link'}
                  </button>
                  <button onClick={() => handleShare('twitter')} role="menuitem">
                    <FaExternalLinkAlt /> Twitter
                  </button>
                  <button onClick={() => handleShare('facebook')} role="menuitem">
                    <FaExternalLinkAlt /> Facebook
                  </button>
                  <button onClick={() => handleShare('email')} role="menuitem">
                    <FaEnvelope /> Email
                  </button>
                </div>
              )}
            </div>
            {isManager && (
              <>
                <button 
                  className={styles.navBtn} 
                  onClick={() => navigate(`/club/${clubId}/edit`)}
                  aria-label="Edit club"
                >
                  <FaEdit /> <span>Edit</span>
                </button>
                <button 
                  className={styles.navBtn} 
                  onClick={() => navigate(`/club/${clubId}/notifications`)}
                  aria-label="View notifications"
                >
                  <FaBell /> <span>Notifications</span>
                </button>
                <button 
                  className={styles.navBtnAccent} 
                  onClick={() => setShowManagementModal(true)}
                  aria-label="Manage members"
                >
                  <FaUsers /> <span>Members</span>
                </button>
              </>
            )}
          </div>
        </nav>

        {/* Hero Content */}
        <div className={styles.heroContent}>
          <div className={styles.heroLeft}>
            {/* Club Logo */}
            <div className={styles.clubLogo}>
              {club.clubLogo ? (
                <img src={club.clubLogo} alt={club.name} />
              ) : (
                <SportIcon />
              )}
            </div>
            
            {/* Club Name & Info */}
            <div className={styles.clubIdentity}>
              <div className={styles.clubTags}>
                <span className={styles.sportTag}><SportIcon /> {club.sport}</span>
                {(club.city || club.state) && (
                  <span className={styles.locationTag}>
                    <FaMapMarkerAlt /> {[club.city, club.state].filter(Boolean).join(', ')}
                  </span>
                )}
              </div>
              <h1 className={styles.clubName}>{club.name}</h1>
              <div className={styles.clubMeta}>
                <span>{club.businessType}</span>
                <span className={styles.metaDot}>•</span>
                <span>{membershipInfo.text}</span>
                {isMember && (
                  <>
                    <span className={styles.metaDot}>•</span>
                    <span className={styles.memberTag}><FaCheck /> {userRole}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className={styles.heroCTA}>
            {isMember ? (
              <button 
                className={styles.ctaPrimary} 
                onClick={() => navigate(`/clubs/${clubId}`)}
                aria-label={`Enter ${club.name}`}
              >
                <FaPlay /> Enter Club
              </button>
            ) : membershipStatus === 'invited' ? (
              <>
                <button 
                  className={styles.ctaPrimary} 
                  onClick={() => setShowJoinModal(true)}
                  aria-label={`Accept invitation to ${club.name}`}
                >
                  <FaEnvelope /> Accept Invitation
                </button>
                <button 
                  className={styles.ctaSecondary} 
                  onClick={() => navigate(`/clubs/${clubId}`)}
                  aria-label="Browse available programs"
                >
                  Browse Programs
                </button>
              </>
            ) : membershipStatus === 'pending' ? (
              <>
                <button 
                  className={styles.ctaPending} 
                  disabled
                  aria-label="Membership pending"
                >
                  <FaClock /> Request Pending
                </button>
                <button 
                  className={styles.ctaSecondary} 
                  onClick={() => navigate(`/clubs/${clubId}`)}
                  aria-label="Browse available programs"
                >
                  Browse Programs
                </button>
              </>
            ) : (
              <>
                <button 
                  className={styles.ctaPrimary} 
                  onClick={() => setShowJoinModal(true)}
                  aria-label={`Join ${club.name}`}
                >
                  <FaUserPlus /> Join Club
                </button>
                <button 
                  className={styles.ctaSecondary} 
                  onClick={() => navigate(`/clubs/${clubId}`)}
                  aria-label="Browse available programs"
                >
                  Browse Programs
                </button>
              </>
            )}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          STATS TICKER - Sports Style
      ═══════════════════════════════════════════════════ */}
      <section className={styles.statsTicker}>
        <div className={styles.tickerInner}>
          <div className={styles.statBlock}>
            <span className={styles.statNumber}>{programs.length}</span>
            <span className={styles.statLabel}>Programs</span>
          </div>
          <div className={styles.statDivider} />
          <div className={styles.statBlock}>
            <span className={styles.statNumber}>{categories.length}</span>
            <span className={styles.statLabel}>Categories</span>
          </div>
          <div className={styles.statDivider} />
          <div className={styles.statBlock}>
            <span className={styles.statNumber}>{memberCount !== null ? memberCount : '—'}</span>
            <span className={styles.statLabel}>Members</span>
          </div>
          <div className={styles.statDivider} />
          <div className={styles.statBlock}>
            <span className={styles.statNumber}>{club.type}</span>
            <span className={styles.statLabel}>Club Type</span>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          ABOUT SECTION
      ═══════════════════════════════════════════════════ */}
      {club.description && (
        <section className={styles.aboutSection}>
          <div className={styles.sectionInner}>
            <h2 className={styles.sectionLabel}>About</h2>
            <p className={styles.aboutText}>{club.description}</p>
          </div>
        </section>
      )}

      {/* ═══════════════════════════════════════════════════
          PROGRAMS SECTION - Featured Cards
      ═══════════════════════════════════════════════════ */}
      <section className={styles.programsSection}>
        <div className={styles.sectionInner}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>
              <span className={styles.titleAccent} />
              Programs
            </h2>
            <button className={styles.viewAllLink} onClick={() => navigate(`/clubs/${clubId}`)}>
              View All <FaChevronRight />
            </button>
          </div>

          {categories.length > 0 ? (
            <div className={styles.programsGrid}>
              {categories.map(category => {
                const count = getProgramCountByCategory(category._id);
                const CategoryIcon = categoryIcons[category.name] || categoryIcons.default;
                
                return (
                  <button
                    key={category._id}
                    className={styles.programCard}
                    onClick={() => navigate(`/clubs/${clubId}?category=${category._id}`)}
                    disabled={count === 0}
                  >
                    <div className={styles.programCardIcon}>
                      <CategoryIcon />
                    </div>
                    <div className={styles.programCardContent}>
                      <h3 className={styles.programCardTitle}>{category.name}</h3>
                      <span className={styles.programCardCount}>
                        {count} {count === 1 ? 'program' : 'programs'}
                      </span>
                    </div>
                    <FaChevronRight className={styles.programCardArrow} />
                  </button>
                );
              })}
            </div>
          ) : (
            <div className={styles.emptyPrograms}>
              <FaFutbol />
              <p>No programs yet. Check back soon!</p>
            </div>
          )}
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          CONTACT FOOTER
      ═══════════════════════════════════════════════════ */}
      <section className={styles.contactSection}>
        <div className={styles.sectionInner}>
          <h2 className={styles.sectionLabel}>Contact & Location</h2>
          
          <div className={styles.contactGrid}>
            {club.contactNumber && (
              <a href={`tel:${club.contactNumber}`} className={styles.contactCard}>
                <FaPhone />
                <span>{club.contactNumber}</span>
              </a>
            )}
            
            {club.email && (
              <a href={`mailto:${club.email}`} className={styles.contactCard}>
                <FaEnvelope />
                <span>{club.email}</span>
              </a>
            )}

            {(club.street || club.city) && (
              <div className={styles.contactCard}>
                <FaMapMarkerAlt />
                <div className={styles.addressLines}>
                  {club.street && <span>{club.street}</span>}
                  <span>{[club.city, club.state, club.zipCode].filter(Boolean).join(', ')}</span>
                  {club.country && <span>{club.country}</span>}
                </div>
              </div>
            )}

            {club.location?.coordinates && (
              <a 
                href={`https://www.google.com/maps/search/?api=1&query=${club.location.coordinates[1]},${club.location.coordinates[0]}`}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.mapButton}
              >
                <FaExternalLinkAlt /> Open in Google Maps
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Modals */}
      {showJoinModal && (
        <JoinClubModal
          club={club}
          onClose={() => setShowJoinModal(false)}
          onJoinSuccess={handleJoinSuccess}
          user={user}
        />
      )}

      {showManagementModal && (
        <ClubManagementModal
          key={managementModalKey}
          club={club}
          onClose={() => {
            setShowManagementModal(false);
            setManagementInitialTab('members');
          }}
          user={user}
          initialTab={managementInitialTab}
        />
      )}
    </div>
  );
};

export default ClubLandingPage;
